import { useCallback, useEffect, useRef } from 'react';
import type { PatientSectionRendererProps } from '@features/patient-page-v3/components/UnsupportedSection';
import { useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { usePatientRequirements } from '../store/usePatientRequirements';

import { PatientRequirementsSectionView } from './PatientRequirementsSectionView';

/**
 * A PHI-safe summary of a failed write, for `error.track`.
 *
 * Deliberately NOT the raw error. The convention in services/statsig/CLAUDE.md
 * puts its first argument straight into `error_message`, and two things here
 * can carry patient text: the waive reason a staff member typed, and a 4xx body
 * that echoes the request back. The status code holds the diagnostic value; the
 * body holds the risk, so only the status travels.
 */
const errorSummary = (thrown: unknown): string => {
  if (typeof thrown === 'object' && thrown !== null && 'status' in thrown) {
    return `status_${String(thrown.status)}`;
  }
  return 'unknown_error';
};

/**
 * The Requirements section on the patient record (GDN-3226; AC-3, AC-4, AC-8,
 * AC-20), mounted by the patient-page-v3 renderer registry.
 *
 * No feature-gate check here on purpose. The page is server-driven: the section
 * only reaches this component because the bootstrap descriptor listed it, and
 * the backend already applies the gate when building that list. Re-checking the
 * client gate would let a client-side "off" hide a section the server chose to
 * send — two authorities for one decision, and the wrong one winning.
 *
 * The heading is not rendered here either: `PatientSectionShell` renders
 * `section.title` from the descriptor, so the title stays server-supplied and
 * this component renders only the body.
 */
export const PatientRequirementsRecordSection = ({
  patientId,
}: PatientSectionRendererProps) => {
  const subject = useSubjectLanguageVariants();
  const organizationId = useOrganizationId();
  const { enqueueSnackbar } = useSnackbar();
  const { patientRequirementsRecord, error: errorTracking } = useEventTracking();

  const {
    rows,
    missingCount,
    deliveryHistory,
    isHistoryError,
    retryHistory,
    isContactError,
    retryContact,
    isLoading,
    isError,
    busyRequirementIds,
    refetch,
    send,
    waive,
    reopen,
  } = usePatientRequirements(patientId, subject.singular);

  // One opened event per mount, once the first load resolves. Firing before the
  // load would report a row count of zero for a section that turned out to be
  // populated.
  const openedTracked = useRef(false);
  useEffect(() => {
    if (openedTracked.current || isLoading || isError) return;
    openedTracked.current = true;
    patientRequirementsRecord.opened({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      row_count: rows.length,
      missing_count: missingCount,
    });
  }, [
    isLoading,
    isError,
    rows.length,
    missingCount,
    organizationId,
    patientId,
    patientRequirementsRecord,
  ]);

  const handleSend = useCallback(
    async (patientRequirementId: string) => {
      const row = rows.find(
        candidate => candidate.patientRequirementId === patientRequirementId,
      );
      patientRequirementsRecord.sendClicked({
        organization_id: organizationId ?? undefined,
        patient_id: patientId,
        action: row?.sendActionLabel,
      });
      try {
        await send(patientRequirementId);
      } catch (thrown) {
        errorTracking.track(errorSummary(thrown), 'patient_requirements_send', {
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
        });
        enqueueSnackbar('Could not send that request. Please try again.', {
          variant: 'error',
        });
      }
    },
    [rows, send, organizationId, patientId, patientRequirementsRecord, errorTracking, enqueueSnackbar],
  );

  const handleWaive = useCallback(
    async (patientRequirementId: string, reason: string) => {
      patientRequirementsRecord.waiveSubmitted({
        organization_id: organizationId ?? undefined,
        patient_id: patientId,
        reason_length: reason.length,
      });
      try {
        await waive(patientRequirementId, reason);
      } catch (thrown) {
        errorTracking.track(errorSummary(thrown), 'patient_requirements_waive', {
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
        });
        enqueueSnackbar('Could not waive that requirement. Please try again.', {
          variant: 'error',
        });
      }
    },
    [waive, organizationId, patientId, patientRequirementsRecord, errorTracking, enqueueSnackbar],
  );

  const handleReopen = useCallback(
    async (patientRequirementId: string) => {
      patientRequirementsRecord.reopenClicked({
        organization_id: organizationId ?? undefined,
        patient_id: patientId,
      });
      try {
        await reopen(patientRequirementId);
      } catch (thrown) {
        errorTracking.track(errorSummary(thrown), 'patient_requirements_reopen', {
          organization_id: organizationId ?? undefined,
          patient_id: patientId,
        });
        enqueueSnackbar('Could not reopen that requirement. Please try again.', {
          variant: 'error',
        });
      }
    },
    [reopen, organizationId, patientId, patientRequirementsRecord, errorTracking, enqueueSnackbar],
  );

  return (
    <PatientRequirementsSectionView
      rows={rows}
      missingCount={missingCount}
      isLoading={isLoading}
      isError={isError}
      deliveryHistory={deliveryHistory}
      isHistoryError={isHistoryError}
      onRetryHistory={retryHistory}
      isContactError={isContactError}
      onRetryContact={retryContact}
      busyRequirementIds={busyRequirementIds}
      subject={subject}
      onSend={handleSend}
      onWaive={handleWaive}
      onReopen={handleReopen}
      onRetry={refetch}
    />
  );
};

export default PatientRequirementsRecordSection;
